import React, { useMemo, useState } from 'react';
import type { OrgTreeData } from '../../services/organization.service';
import type { Company, Department, Team } from '../../types/organization.types';
import { cn } from '../../lib/cn';

// Structured node reference passed through the selection and drop callbacks.
// Deliberately NOT a composite "type-uuid" string: the old tree encoded node
// identity that way, and splitting it back apart truncated the UUIDs.
export interface OrgNodeRef {
  type: 'company' | 'team' | 'department';
  id: string;
  name: string;
}

export interface LedgerTreeProps {
  data: OrgTreeData;
  selected: OrgNodeRef | null;
  onSelect: (node: OrgNodeRef | null) => void;
  /**
   * Drop side of the row-to-tree assignment contract. The user id is read
   * from `event.dataTransfer.getData('text/plain')`, so the drag source —
   * `UsersTable.onRowDragStart` — must have called
   * `setData('text/plain', user.id)` (and set `effectAllowed = 'move'`).
   * Nothing enforces the pairing at runtime — a mismatched key fails
   * silently, `getData` returns '' and the drop is ignored.
   */
  onUserDrop?: (userId: string, node: OrgNodeRef) => void;
}

// Internal map key only — never rendered, never parsed back apart.
const nodeKey = (type: OrgNodeRef['type'], id: string) => `${type}:${id}`;

const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name);

interface Hierarchy {
  companies: Company[];
  teamsByCompany: Map<string, Team[]>;
  departmentsByTeam: Map<string, Department[]>;
}

const buildHierarchy = (data: OrgTreeData): Hierarchy => {
  const teamsByCompany = new Map<string, Team[]>();
  for (const team of data.teams) {
    const siblings = teamsByCompany.get(team.company) ?? [];
    siblings.push(team);
    teamsByCompany.set(team.company, siblings);
  }
  const departmentsByTeam = new Map<string, Department[]>();
  for (const department of data.departments) {
    const siblings = departmentsByTeam.get(department.team) ?? [];
    siblings.push(department);
    departmentsByTeam.set(department.team, siblings);
  }
  teamsByCompany.forEach((teams) => teams.sort(byName));
  departmentsByTeam.forEach((departments) => departments.sort(byName));
  return { companies: [...data.companies].sort(byName), teamsByCompany, departmentsByTeam };
};

// Two counts per node.
//
// `direct` is who is bound at that node or below it: a role binds exactly one
// level, and deeper bindings roll up through the hierarchy edges (department
// -> its team -> that team's company). Parents are resolved via the
// teams/departments arrays, never via the role's own company field, because
// department roles arrive with a backfilled company but the team edge is what
// places them.
//
// `reach` adds the other direction: a company-level role reaches every team
// and department beneath it, a team-level role every department in the team.
// Rolling up without ever pushing down is why every department read 0 on a
// tenant whose roles all sit at company level. True, and useless.
interface UserCounts {
  direct: Map<string, Set<string>>;
  reach: Map<string, Set<string>>;
}

const buildUserSets = (data: OrgTreeData): UserCounts => {
  const teamById = new Map(data.teams.map((team) => [team.id, team]));
  const departmentById = new Map(data.departments.map((d) => [d.id, d]));
  const direct = new Map<string, Set<string>>();
  const reach = new Map<string, Set<string>>();

  const addTo = (target: Map<string, Set<string>>, key: string, userId: string) => {
    let set = target.get(key);
    if (!set) {
      set = new Set<string>();
      target.set(key, set);
    }
    set.add(userId);
  };
  // Everything bound at or below a node also counts as reaching it.
  const add = (key: string, userId: string) => {
    addTo(direct, key, userId);
    addTo(reach, key, userId);
  };

  for (const role of data.userRoles) {
    if (role.department) {
      add(nodeKey('department', role.department), role.user);
      const department = departmentById.get(role.department);
      if (!department) continue;
      add(nodeKey('team', department.team), role.user);
      const team = teamById.get(department.team);
      if (team) add(nodeKey('company', team.company), role.user);
    } else if (role.team) {
      add(nodeKey('team', role.team), role.user);
      const team = teamById.get(role.team);
      if (team) add(nodeKey('company', team.company), role.user);
    } else if (role.company) {
      add(nodeKey('company', role.company), role.user);
    }
  }

  for (const role of data.userRoles) {
    if (role.department) continue;
    if (role.team) {
      for (const department of data.departments) {
        if (department.team === role.team) {
          addTo(reach, nodeKey('department', department.id), role.user);
        }
      }
    } else if (role.company) {
      for (const team of data.teams) {
        if (team.company !== role.company) continue;
        addTo(reach, nodeKey('team', team.id), role.user);
        for (const department of data.departments) {
          if (department.team === team.id) {
            addTo(reach, nodeKey('department', department.id), role.user);
          }
        }
      }
    }
  }

  return { direct, reach };
};

type DropHandlers = Pick<React.HTMLAttributes<HTMLDivElement>, 'onDragOver' | 'onDragLeave' | 'onDrop'>;

interface NodeRowProps {
  node: OrgNodeRef;
  counts: { direct: number; reach: number };
  labelClassName: string;
  /** undefined = leaf: no chevron, no aria-expanded. */
  expanded?: boolean;
  onToggle?: () => void;
  isSelected: boolean;
  onSelect: () => void;
  dropActive: boolean;
  dropHandlers?: DropHandlers;
  children?: React.ReactNode;
}

// Plain nested lists on purpose: tree/treeitem/group roles promise the
// roving-tabindex + arrow-key behavior of the ARIA tree pattern, which this
// widget does not implement (and nesting buttons inside treeitems is its own
// anti-pattern). Selection is announced via aria-current on the name button —
// the register-table idiom — and expansion state lives honestly on the
// chevron buttons as aria-expanded.
const NodeRow: React.FC<NodeRowProps> = (props) => {
  const { node, counts, expanded, isSelected, dropActive } = props;
  return (
    <li>
      <div
        data-testid="tree-row"
        data-drop-active={dropActive ? 'true' : undefined}
        {...props.dropHandlers}
        className={cn(
          'flex items-center gap-1 hover:bg-accent-soft',
          'data-drop-active:bg-accent-soft data-drop-active:shadow-[inset_2px_0_0_var(--color-accent)]',
          isSelected && 'bg-accent-soft shadow-[inset_2px_0_0_var(--color-accent)]',
        )}
      >
        {expanded === undefined ? (
          <span aria-hidden className="w-4 shrink-0" />
        ) : (
          <button
            type="button"
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${node.name}`}
            aria-expanded={expanded}
            onClick={props.onToggle}
            className="w-4 shrink-0 cursor-pointer text-scale-0 text-ink-muted hover:text-ink"
          >
            <span aria-hidden>{expanded ? '▾' : '▸'}</span>
          </button>
        )}
        <button
          type="button"
          onClick={props.onSelect}
          aria-current={isSelected ? 'true' : undefined}
          className="flex min-w-0 flex-1 cursor-pointer items-baseline justify-between gap-2 py-1 pr-1 text-left"
        >
          <span data-testid="tree-name" className={cn('truncate', props.labelClassName)}>
            {node.name}
          </span>
          {/* Two numbers only when they differ. "0 · 10" reads as nobody is
              bound here and ten people can reach it, which is the truth on a
              tenant whose roles all sit at company level. */}
          <span
            data-testid="tree-count"
            title={
              counts.reach === counts.direct
                ? `${counts.direct} assigned here`
                : `${counts.direct} assigned here, ${counts.reach} with access`
            }
            className="shrink-0 text-right font-mono text-scale-0 text-ink-muted"
          >
            {counts.reach === counts.direct
              ? counts.direct
              : `${counts.direct} · ${counts.reach}`}
          </span>
        </button>
      </div>
      {expanded && props.children ? (
        <ul className="ml-2 border-l border-rule-faint pl-2">{props.children}</ul>
      ) : null}
    </li>
  );
};

export const LedgerTree: React.FC<LedgerTreeProps> = ({ data, selected, onSelect, onUserDrop }) => {
  const { companies, teamsByCompany, departmentsByTeam } = useMemo(() => buildHierarchy(data), [data]);
  const userSets = useMemo(() => buildUserSets(data), [data]);

  // Expansion is stored as deviations from the per-level default (companies
  // open, teams closed), so freshly loaded nodes land in their default state.
  const [toggledKeys, setToggledKeys] = useState<ReadonlySet<string>>(new Set());
  const [dropKey, setDropKey] = useState<string | null>(null);

  if (companies.length === 0) {
    return <p className="py-6 font-serif text-scale-1 italic text-ink-muted">No organizations.</p>;
  }

  const countFor = (node: OrgNodeRef) => {
    const key = nodeKey(node.type, node.id);
    return {
      direct: userSets.direct.get(key)?.size ?? 0,
      reach: userSets.reach.get(key)?.size ?? 0,
    };
  };
  const isSelected = (node: OrgNodeRef) => selected?.type === node.type && selected.id === node.id;
  const select = (node: OrgNodeRef) => () => onSelect(isSelected(node) ? null : node);

  const isExpanded = (node: OrgNodeRef) => {
    const toggled = toggledKeys.has(nodeKey(node.type, node.id));
    return node.type === 'company' ? !toggled : toggled;
  };
  const toggle = (node: OrgNodeRef) => () =>
    setToggledKeys((prev) => {
      const next = new Set(prev);
      const key = nodeKey(node.type, node.id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const dropHandlersFor = (node: OrgNodeRef): DropHandlers | undefined => {
    if (!onUserDrop) return undefined;
    const key = nodeKey(node.type, node.id);
    return {
      onDragOver: (event) => {
        event.preventDefault();
        setDropKey(key);
      },
      onDragLeave: () => setDropKey((prev) => (prev === key ? null : prev)),
      onDrop: (event) => {
        event.preventDefault();
        const userId = event.dataTransfer.getData('text/plain');
        if (userId) onUserDrop(userId, node);
        setDropKey(null);
      },
    };
  };

  const rowProps = (node: OrgNodeRef) => ({
    node,
    counts: countFor(node),
    isSelected: isSelected(node),
    onSelect: select(node),
    dropActive: dropKey === nodeKey(node.type, node.id),
    dropHandlers: dropHandlersFor(node),
  });

  return (
    <ul aria-label="Organizations" className="select-none">
      {companies.map((company) => {
        const companyRef: OrgNodeRef = { type: 'company', id: company.id, name: company.name };
        const teams = teamsByCompany.get(company.id) ?? [];
        return (
          <NodeRow
            key={company.id}
            {...rowProps(companyRef)}
            labelClassName="cap-label"
            expanded={teams.length > 0 ? isExpanded(companyRef) : undefined}
            onToggle={toggle(companyRef)}
          >
            {teams.length > 0
              ? teams.map((team) => {
                  const teamRef: OrgNodeRef = { type: 'team', id: team.id, name: team.name };
                  const departments = departmentsByTeam.get(team.id) ?? [];
                  return (
                    <NodeRow
                      key={team.id}
                      {...rowProps(teamRef)}
                      labelClassName="text-scale-1 text-ink"
                      expanded={departments.length > 0 ? isExpanded(teamRef) : undefined}
                      onToggle={toggle(teamRef)}
                    >
                      {departments.length > 0
                        ? departments.map((department) => (
                            <NodeRow
                              key={department.id}
                              {...rowProps({ type: 'department', id: department.id, name: department.name })}
                              labelClassName="text-scale-1 text-ink-muted"
                            />
                          ))
                        : undefined}
                    </NodeRow>
                  );
                })
              : undefined}
          </NodeRow>
        );
      })}
    </ul>
  );
};
